import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { contactApi } from '../utils/api';
import SectionHeading from '../components/common/SectionHeading';
import { MapPin, Mail, Phone, Send } from 'lucide-react';

function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error('सन्देश लेख्नुहोस्।');
      return;
    }
    setSending(true);
    try {
      await contactApi.create(form);
      toast.success('सन्देश पठाइयो। धन्यवाद!');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'सन्देश पठाउन असफल भयो।');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full border border-line rounded-lg px-3 py-2.5 text-sm outline-none bg-white transition-all duration-200 focus:border-blue focus:ring-2 focus:ring-blue/20 focus:-translate-y-0.5';

  return (
    <main className="page max-w-[1240px] mx-auto px-5 pt-7 pb-16 w-full">
      <SectionHeading title="सम्पर्क गर्नुहोस्" subtitle="तपाईंका सुझाव, प्रश्न वा सन्देश हामीलाई पठाउनुहोस्।" />
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mt-8">

        {/* Left column — info cards */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-line rounded-2xl p-6 shadow-sm transition-all duration-300 hover:border-green-500 hover:shadow-lg hover:-translate-y-1 group">
            <h3 className="font-display text-xl font-bold mb-4 transition-colors group-hover:text-blue">सम्पर्क विवरण</h3>
            <div className="space-y-4">
              {[
                { Icon: MapPin, label: 'कायालय', value: 'काठमाडौं, नेपाल', circle: 'bg-emerald-100 text-emerald-700', ring: 'group-hover:bg-emerald-500' },
                { Icon: Mail, label: 'इमेल', value: 'contact@kalojhola.com', circle: 'bg-blue-100 text-blue-700', ring: 'group-hover:bg-blue' },
                { Icon: Phone, label: 'फोन', value: '+९७७ १ ४४४५५५६', circle: 'bg-amber-100 text-amber-700', ring: 'group-hover:bg-amber-500' },
              ].map(({ Icon, label, value, circle, ring }) => (
                <div key={label} className="flex items-start gap-3 rounded-xl -mx-2 px-2 py-1.5 transition-colors duration-200 hover:bg-paper-soft cursor-pointer">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${circle} transition-colors duration-300 ${ring.replace('group-', 'hover:')}`}>
                    <Icon size={18} className={`transition-colors duration-300 ${ring.includes('emerald') ? 'hover:text-white' : ''} group-hover:text-white`} />
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{label}</p>
                    <p className="text-muted text-sm group-hover:text-ink-soft transition-colors">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-line rounded-2xl p-6 shadow-sm transition-all duration-300 hover:border-blue hover:shadow-lg hover:-translate-y-1 group">
            <h3 className="font-display text-xl font-bold mb-4 transition-colors group-hover:text-green-600">कार्यालय समय</h3>
            <div className="space-y-2 text-sm">
              {[
                ['सोमबार - शुक्रबार', '९:०० - १७:००'],
                ['शनिबार', '९:०० - १३:००'],
                ['आइतबार', 'बन्द'],
              ].map(([day, time]) => (
                <div key={day} className="flex justify-between rounded-lg px-2 py-1.5 -mx-2 transition-colors duration-200 hover:bg-emerald-50 cursor-pointer">
                  <span className="text-muted group-hover:text-ink-soft transition-colors">{day}</span>
                  <span className="font-semibold text-ink group-hover:text-green-700 transition-colors">{time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column — message form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-line rounded-2xl p-6 md:p-8 shadow-sm transition-all duration-300 hover:shadow-md group/form">
            <h3 className="font-display text-xl font-bold mb-6 relative inline-block text-ink transition-colors duration-300 hover:text-blue cursor-default">
              सन्देश पठाउनुहोस्
              <span className="absolute left-0 -bottom-1 h-[3px] w-0 rounded-full bg-gradient-to-r from-green-500 to-blue transition-all duration-300 group-hover/form:w-full"></span>
            </h3>
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  type="text"
                  placeholder="तपाईंको नाम"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
                <input
                  type="email"
                  placeholder="इमेल"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>
              <input
                type="tel"
                placeholder="फोन नम्बर"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
              <textarea
                rows={5}
                placeholder="तपाईंको सन्देश..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-y`}
              />
              <button
                type="submit"
                disabled={sending}
                className="self-start inline-flex items-center gap-1.5 bg-crimson text-white text-[13px] font-bold px-6 py-3 rounded-lg transition-all duration-200 hover:bg-blue hover:shadow-lg hover:shadow-blue/30 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:-translate-y-0"
              >
                <Send size={16} className="transition-transform duration-200 group-hover/form:translate-x-0.5" /> पठाउनुहोस्
              </button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;